/**
 * Como o caixa escreve valores, meses e percentuais.
 *
 * Papel: aqui ficam o `fmt` da origem (`formatarReais`), o valor de uma linha
 * com o sinal do tipo (`valorDaLinha`), as quatro casas da diária
 * (`formatarAteQuatroCasas`), o mês abreviado do Fechamento, o andar de mês em
 * mês das setas ‹ ›, o mês de hoje e o percentual das tabelas por categoria.
 *
 * Como usar: todo valor em reais da tela passa por `formatarReais`, e não pelo
 * `formatarDinheiro` de `shared/lib/formato.ts`, que escreve `-R$` com hífen e
 * o espaço da moeda do `Intl`. Onde a origem põe o sinal por conta própria — a
 * coluna Valor, com `+` e `-` pelo tipo, e o "-R$" das saídas da barra de lote
 * —, quem chama passa o valor absoluto e escreve o sinal antes.
 *
 * O negativo sai com o sinal de menos tipográfico (`−`, U+2212) antes do
 * "R$", que é a exceção 11 da spec: a origem escrevia o valor absoluto, e um
 * Saldo Inicial negativo saía igual a um positivo. A cor de cada número
 * continua a da origem, e é quem desenha que a escolhe.
 */
import { type TipoLancamento, entraNoCaixa, valorDoLancamento } from '@concierge/shared';

import { hojeEmIso } from '@/shared/lib/vencimento';
import { NOMES_DOS_MESES } from './rotulos';

/** O sinal de menos tipográfico do valor negativo. */
const SINAL_DE_MENOS = '−';

/**
 * Escreve um valor em reais como o `fmt` da origem (l. 1661), com o sinal de
 * menos no negativo.
 *
 * @param valor - O valor em reais; o que não é número vale zero, como o
 *   `v||0` da origem.
 * @returns `R$ 1.234,50`, ou `−R$ 1.234,50` no negativo. O negativo que
 *   arredonda para zero sai sem sinal.
 */
export function formatarReais(valor: number): string {
  const centavos = Number.isFinite(valor) ? Math.round(valor * 100) : 0;
  const absoluto = Math.abs(centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${centavos < 0 ? SINAL_DE_MENOS : ''}R$ ${absoluto}`;
}

/**
 * Escreve o valor final de um lançamento com o sinal do tipo, como a coluna
 * Valor da tabela (l. 3076, 3097).
 *
 * É o texto da coluna e o do rótulo que segue o ponteiro no arraste, que a
 * origem copia da célula; os dois deverão continuar iguais.
 *
 * @param lancamento - O tipo e os três valores do lançamento.
 * @returns `+R$ 50,00` no que entra, `-R$ 100,00` no que sai, com hífen.
 */
export function valorDaLinha(lancamento: {
  tipo: TipoLancamento;
  valorBruto: number;
  desconto: number;
  acrescimo: number;
}): string {
  return `${entraNoCaixa(lancamento.tipo) ? '+' : '-'}${formatarReais(valorDoLancamento(lancamento))}`;
}

/**
 * Escreve uma diária com duas a quatro casas, como o `_acFmtDiaria` da origem
 * (l. 5015).
 *
 * Quem chama é o perfil do favorecido, ao abrir, para pôr no campo a diária
 * gravada; a mesma escrita volta para o número por `lerValorDigitado`.
 *
 * @param valor - A diária; nula vale zero.
 * @returns `83,3333`, `250,00`, `1.234,50`.
 */
export function formatarAteQuatroCasas(valor: number | null): string {
  return (valor ?? 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Escreve a competência como o rótulo do Fechamento (l. 3149).
 *
 * @param competencia - O mês, em `AAAA-MM`.
 * @returns As três primeiras letras do mês em caixa alta e o ano: `MAR 2026`.
 */
export function rotuloDoMes(competencia: string): string {
  const nome = NOMES_DOS_MESES[Number(competencia.slice(5, 7)) - 1] ?? '';
  return `${nome.slice(0, 3).toUpperCase()} ${competencia.slice(0, 4)}`;
}

/**
 * Anda a competência para a frente ou para trás, como o `navigate` da origem
 * (l. 1823).
 *
 * @param competencia - O mês de partida, em `AAAA-MM`.
 * @param meses - Quantos meses andar; negativo volta.
 * @returns O mês de chegada, em `AAAA-MM`, virando o ano quando preciso.
 */
export function somarMeses(competencia: string, meses: number): string {
  const indice =
    Number(competencia.slice(0, 4)) * 12 + Number(competencia.slice(5, 7)) - 1 + meses;
  const ano = Math.floor(indice / 12);
  return `${String(ano)}-${String(indice - ano * 12 + 1).padStart(2, '0')}`;
}

/**
 * O mês de hoje, em hora local.
 *
 * É o mês com que o caixa abre (l. 1553). Sai de `hojeEmIso`, e não de
 * `toISOString`: às 22h do último dia do mês, em Brasília, o UTC já está no mês
 * seguinte.
 *
 * @returns O mês de hoje, em `AAAA-MM`.
 */
export function competenciaDeHoje(): string {
  return hojeEmIso().slice(0, 7);
}

/**
 * Escreve a parte de um total como as tabelas por categoria (l. 2635).
 *
 * @param valor - A parte.
 * @param total - O todo; zero dá `0.0%`.
 * @returns O percentual com uma casa e ponto decimal, como o `toFixed(1)` da
 *   origem: `12.3%`.
 */
export function formatarPercentual(valor: number, total: number): string {
  return `${(total > 0 ? (valor / total) * 100 : 0).toFixed(1)}%`;
}
